import {
  type ComponentChildren,
  type ComponentProps,
  createContext,
  type FunctionComponent,
  type TargetedEvent,
  type TargetedFocusEvent,
  type TargetedKeyboardEvent,
  type VNode,
} from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { useDropdown } from 'Core/hooks/index';
import { FacetValue } from 'Models';
import { cloneSafe } from 'Utils/components';
import isMobile from 'Vendor/isMobile';

type PropsWithChildren<T extends object = object> = T & {
  children?: ComponentChildren;
};

const stub = () => {
  /* stub */
};
const selectContext = createContext({
  activate: stub as (key: string) => void,
  reset: stub as () => void,
  getDisabled: (() => false) as (key: string) => boolean,
});

export interface Entry {
  value: string;
  term: string;
  selected: boolean;
  payload?: string | null;
  hitCount?: number;
  url?: string | null;
}

export interface Params {
  activeIndex: number;
  disabled?: boolean;
  entries: Entry[];
  onChange: (e: string | TargetedEvent<HTMLSelectElement, Event>) => void;
  extraClassName: string;
  firstSelectedTerm?: string;
  hideNullOption?: boolean;
  loading?: boolean;
  selectedEntries: Entry[];
  showCheckboxes?: boolean;
  showHitCount?: boolean;
  index?: number;
  title: string;
  field: string;
  useNativeDropdown?: boolean;
  isDropdownVisible: boolean;
  isSearchable: boolean;
  filterInput: TemplateFunctionInvoker<void>;
}

export interface SelectConfig {
  field: string;
  title: string;
  entries: Entry[];
  disabled?: boolean;
  extraClassName?: string;
  hideNullOption?: boolean;
  loading?: boolean;
  onChange: (value: string) => void;
  showCheckboxes?: boolean;
  showHitCount?: boolean;
  index?: number;
  useNativeDropdown?: boolean;
  isSearchable?: boolean;
  initialOpen?: boolean;
}

export interface Props extends SelectConfig {
  template: TemplateFunction<Params>;
}

const Select: FunctionComponent<Props> = ({
  template,
  disabled: disabled_pr,
  entries,
  extraClassName = '',
  hideNullOption,
  loading,
  showCheckboxes,
  showHitCount,
  index,
  field,
  title,
  onChange: onChange_pr,
  useNativeDropdown: useNative,
  isSearchable = false,
  initialOpen = false,
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const { activate, reset, getDisabled } = useContext(selectContext);
  const [filterValue, setFilterValue] = useState('');
  const [isFilterInputFocused, setIsFilterInputFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const disabled = disabled_pr || getDisabled(title);

  const useNativeDropdown = useNative ?? true;

  const {
    showDropdown,
    hideDropdown,
    DO_NOT_USE_visible: isDropdownVisible,
  } = useDropdown(disabled || useNativeDropdown ? { current: null } : rootRef, '.cm_select', {
    toggle: !isSearchable,
  });

  const selectedEntries = entries.filter((e) => e.selected) || [];
  const firstSelectedTerm = selectedEntries[0]?.term;

  const filteredEntries =
    isSearchable && filterValue
      ? entries.filter((e) => e.value.toLowerCase().includes(filterValue.toLowerCase()))
      : entries;

  useEffect(() => {
    if (selectedEntries.length) {
      setFilterValue('');
    }
  }, [selectedEntries.length]);

  useEffect(
    function scrollToActiveOption() {
      const optionToScroll = rootRef.current?.querySelector(`.option:nth-of-type(${activeIndex + 1})`);
      optionToScroll?.scrollIntoView({ block: 'nearest' });
    },
    [activeIndex],
  );

  useEffect(
    function autoOpenDropdown() {
      if (initialOpen && !disabled && !useNativeDropdown && entries.length > 0) {
        const timer = setTimeout(() => {
          showDropdown();
          if (isSearchable) {
            const input = rootRef.current?.querySelector<HTMLInputElement>(`#cm_filter-input-${field}`);
            input?.focus();
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    },
    [initialOpen, disabled, useNativeDropdown, entries.length, showDropdown, isSearchable, field],
  );

  const onFocus = (e: TargetedFocusEvent<HTMLElement>) => {
    if (isMobile && e.target instanceof HTMLSelectElement) {
      activate(title);
    }
  };

  const onBlur = (e: TargetedFocusEvent<HTMLElement>) => {
    if (isMobile && e.target instanceof HTMLSelectElement) {
      reset();
    }
  };

  const onChange = (e: string | TargetedEvent<HTMLSelectElement, Event>) => {
    if (typeof e === 'string') {
      onChange_pr(e);
      hideDropdown();
      return;
    }

    onChange_pr(e.currentTarget.value);
    e.currentTarget.blur();
  };

  const handleKeyDown = (e: TargetedKeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        setActiveIndex((prevIndex) => (prevIndex + 1) % filteredEntries.length);
        e.preventDefault();
        break;
      case 'ArrowUp':
        setActiveIndex((prevIndex) => (prevIndex - 1 + filteredEntries.length) % filteredEntries.length);
        e.preventDefault();
        break;
      case 'Enter':
        onChange_pr(filteredEntries[activeIndex].term);
        hideDropdown();
        setTimeout(() => {
          const currentInput = rootRef.current?.querySelector('input');
          currentInput?.blur();

          const nextInput = rootRef.current?.nextElementSibling?.querySelector('input');
          nextInput?.focus();
        }, 300);
        break;
      default:
        setActiveIndex(0);
        break;
    }
  };

  const translatedTitle = FacetValue.translateFacetTitle(field, title);

  const filterInput = (templ: TemplateFunction<void>) => {
    if (!isSearchable) {
      return null;
    }

    const selectedEntryIndex = entries.findIndex((entry) => entry.selected);

    const templateProps = templ()?.props as ComponentProps<'input'>;

    const props = {
      ...templateProps,
      type: 'text',
      autoComplete: 'off',
      id: `cm_filter-input-${field}`,
      className: `cm_select__pretty ${templateProps?.className}`,
      placeholder: templateProps?.placeholder ?? translatedTitle,
      value: isFilterInputFocused ? filterValue : selectedEntries.map((e) => e.value).join(', ') || '',
      disabled,
      onChange: (e: TargetedEvent<HTMLInputElement, Event>) => setFilterValue(e.currentTarget.value),
      onKeyDown: (e: TargetedKeyboardEvent<HTMLInputElement>) => handleKeyDown(e),
      onFocus: () => {
        if (selectedEntryIndex >= 0) {
          setActiveIndex(selectedEntryIndex);
        }

        setIsFilterInputFocused(true);
        showDropdown();
      },
      onClick: () => {
        if (!disabled) {
          showDropdown();
        }
      },
      onBlur: () => {
        if (selectedEntryIndex < 0) {
          setActiveIndex(0);
        }

        setIsFilterInputFocused(false);
        setTimeout(() => setFilterValue(''), 200);
      },
    };

    return (<input {...props} key={field} />) as TemplateResult;
  };

  const component = template.call({
    activeIndex,
    disabled,
    entries: filteredEntries,
    extraClassName,
    firstSelectedTerm,
    hideNullOption,
    loading,
    selectedEntries,
    showCheckboxes,
    showHitCount,
    index,
    field,
    title: translatedTitle,
    onChange,
    useNativeDropdown,
    isDropdownVisible,
    isSearchable,
    filterInput,
  });

  return cloneSafe(component, rootRef, {
    appendedClasses: 'cm_select',
    onFocus,
    onBlur,
  });
};

export function SelectContextProvider({ children }: PropsWithChildren): VNode<object> | null {
  const [active, setActive] = useState<string | null>(null);
  const context = useMemo(
    () => ({
      activate: (key: string) => setActive(key),
      reset: () => setActive(null),
      getDisabled: (key: string) => !!active && key !== active,
    }),
    [active],
  );

  return <selectContext.Provider value={context}>{children}</selectContext.Provider>;
}

type SelectRepeaterConfig = (SelectConfig & { key?: string }) | null;

export function createSelectRepeater(propsArray: SelectRepeaterConfig[]): RepeaterFunctionInvoker<Params> {
  return propsArray.map((props, index) => {
    if (props) {
      const { key, ...restProps } = props;
      return (templ: TemplateFunction<Params>) =>
        (<Select template={templ} {...restProps} key={key || index} />) as TemplateResult;
    }

    return () => null;
  });
}

export default Select;
